import { Component, OnInit, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { UrlUtils, ProtocolUtils } from '@azure/msal-common';

@Component({
  selector: 'app-redirect',
  template: '',
})
export class B2CRedirectComponent implements OnInit {
  private authService = inject(MsalService);

  constructor() {}

  ngOnInit(): void {
    this.authService.getLogger().verbose('MsalRedirectComponent activated', '');
    this.authService.handleRedirectObservable().subscribe((authResult) => {
      if (this.forwardToRequestOrigin()) {
        return;
      }

      // The response was already processed on this origin but we are still sitting on the blank
      // /auth landing. A full reload to the app root re-bootstraps MSAL so inProgress$ emits
      // InteractionStatus.None off /auth and the auth state gets established; an in-app navigation
      // would leave the app unauthenticated because that broadcast event never fires again.
      if (authResult == null && window.location.pathname.startsWith('/auth')) {
        window.location.href = window.location.origin + '/';
      }
    });
  }

  /**
   * In a cross-origin B2C deployment the response lands on the redirectUri origin while the login
   * was started on a different origin carried in the request state. Forward the full hash there
   * (a real navigation that reloads the page) so MSAL on the initiating origin can process it.
   * Returns true when a navigation was started.
   */
  private forwardToRequestOrigin(): boolean {
    const redirectHash: string = (<any>this.authService).redirectHash;
    if (!redirectHash) {
      return false;
    }

    try {
      const deserializedResponse = UrlUtils.getDeserializedResponse(redirectHash);
      if (deserializedResponse?.state == null) {
        return false;
      }

      const { userRequestState } = ProtocolUtils.parseRequestState(
        (<any>this.authService.instance).controller.browserCrypto.base64Decode,
        deserializedResponse.state,
      );

      if (this.isSafeOrigin(userRequestState) && !window.location.href.startsWith(userRequestState)) {
        window.location.href = userRequestState + '/auth' + redirectHash;
        return true;
      }
    } catch (error) {
      this.authService.getLogger().error(`Auth redirect state processing failed: ${error}`, '');
    }

    return false;
  }

  /**
   * Only a bare http(s) origin (e.g. https://host:port) is an acceptable forward target. This
   * rejects path-bearing URLs and javascript:/data: URIs, closing the open-redirect/XSS sink that
   * assigning an attacker-supplied state value straight to location.href would otherwise open.
   */
  private isSafeOrigin(value: string | null | undefined): value is string {
    if (!value) {
      return false;
    }
    try {
      const url = new URL(value);
      return (url.protocol === 'https:' || url.protocol === 'http:') && url.origin === value;
    } catch {
      return false;
    }
  }
}
